/**
 * Parsing the old hand-kept timesheets.
 *
 * The two source files disagree about almost everything: column order, column
 * names, date format, and month labels (one uses Hebrew). They also carry
 * blank filler rows and at least one date with the year missing. So this parses
 * defensively and reports what it could not understand rather than guessing
 * silently — a wrong date in a billing record is worse than a skipped row.
 */

import type { EntryInput } from '$shared/types';
import { isValidIsoDate } from '$shared/dates';

export interface ParsedRow {
  date: string;
  hours: number;
  notes: string;
  /** Set when the row parsed, but with an assumption worth showing the user. */
  warning?: string;
}

export interface ParseResult {
  rows: ParsedRow[];
  skipped: { line: number; raw: string; reason: string }[];
  /** Which date order was detected, and how confidently. */
  dateOrder: DateOrder;
  ambiguous: boolean;
  columns: ColumnRoles;
}

export type DateOrder = 'dmy' | 'mdy' | 'iso';

interface ColumnRoles {
  date: number;
  hours: number;
  notes: number;
}

const HEADER_PATTERNS: Record<keyof ColumnRoles, RegExp> = {
  date: /^\s*(date|תאריך)\s*$/i,
  hours: /^\s*(hours?|שעות)\s*$/i,
  notes: /^\s*(notes?|description|details|comments?|תיאור|הערות)\s*$/i,
};

/** Split pasted TSV or an uploaded CSV into a grid. */
export function parseDelimited(text: string): string[][] {
  const delimiter = detectDelimiter(text);
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];

    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++;
        } else quoted = false;
      } else cell += ch;
      continue;
    }

    if (ch === '"') quoted = true;
    else if (ch === delimiter) {
      row.push(cell);
      cell = '';
    } else if (ch === '\n') {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else if (ch !== '\r') cell += ch;
  }

  if (cell || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}

function detectDelimiter(text: string): string {
  // Sheets copies to the clipboard as TSV; a downloaded export is CSV.
  const sample = text.slice(0, 4000);
  const tabs = (sample.match(/\t/g) ?? []).length;
  const commas = (sample.match(/,/g) ?? []).length;
  return tabs > commas ? '\t' : ',';
}

/** Find the header row and which column holds what. */
export function detectColumns(grid: string[][]): { headerRow: number; columns: ColumnRoles } {
  for (let r = 0; r < Math.min(grid.length, 10); r++) {
    const cells = grid[r].map((c) => c.trim());
    const roles: Partial<ColumnRoles> = {};

    cells.forEach((cell, i) => {
      for (const key of ['date', 'hours', 'notes'] as const) {
        if (roles[key] === undefined && HEADER_PATTERNS[key].test(cell)) roles[key] = i;
      }
    });

    if (roles.date !== undefined && roles.hours !== undefined) {
      return {
        headerRow: r,
        // A notes column is genuinely optional — some months have none filled in.
        columns: { date: roles.date, hours: roles.hours, notes: roles.notes ?? -1 },
      };
    }
  }

  // No recognisable header: fall back to the shape both files share.
  return { headerRow: -1, columns: { date: 0, hours: 1, notes: 2 } };
}

/**
 * Work out whether dates are day-first or month-first by looking for a value
 * that can only be read one way (a component above 12).
 */
export function detectDateOrder(values: string[]): { order: DateOrder; ambiguous: boolean } {
  let dayFirst = 0;
  let monthFirst = 0;
  let iso = 0;

  for (const raw of values) {
    const v = raw.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
      iso++;
      continue;
    }
    const m = v.match(/^(\d{1,2})[/.\-](\d{1,2})(?:[/.\-](\d{2,4}))?$/);
    if (!m) continue;
    const a = Number(m[1]);
    const b = Number(m[2]);
    if (a > 12) dayFirst++;
    else if (b > 12) monthFirst++;
  }

  if (iso && !dayFirst && !monthFirst) return { order: 'iso', ambiguous: false };
  if (dayFirst && !monthFirst) return { order: 'dmy', ambiguous: false };
  if (monthFirst && !dayFirst) return { order: 'mdy', ambiguous: false };
  // Either nothing decisive, or contradictory evidence — make the user choose.
  return { order: 'dmy', ambiguous: true };
}

/** Parse one date cell. Returns null when it cannot be read at all. */
export function parseLegacyDate(
  raw: string,
  order: DateOrder,
  fallbackYear?: number,
): { date: string; warning?: string } | null {
  const v = raw.trim();
  if (!v) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return isValidIsoDate(v) ? { date: v } : null;

  const m = v.match(/^(\d{1,2})[/.\-](\d{1,2})(?:[/.\-](\d{2,4}))?$/);
  if (!m) return null;

  const first = Number(m[1]);
  const second = Number(m[2]);
  let day: number;
  let month: number;

  if (order === 'mdy') {
    month = first;
    day = second;
  } else {
    day = first;
    month = second;
  }

  let warning: string | undefined;
  let year: number;

  if (m[3]) {
    year = Number(m[3]);
    if (year < 100) year += year < 70 ? 2000 : 1900;
  } else if (fallbackYear) {
    // The ActiveFence sheet has a "14/10" with no year. Inherit from the row
    // above rather than dropping a real billed day, but say so.
    year = fallbackYear;
    warning = `No year in "${v}" — assumed ${fallbackYear}`;
  } else return null;

  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return isValidIsoDate(iso) ? { date: iso, warning } : null;
}

/** Parse a whole pasted/uploaded sheet into importable entries. */
export function parseLegacySheet(text: string, forcedOrder?: DateOrder): ParseResult {
  const grid = parseDelimited(text);
  const { headerRow, columns } = detectColumns(grid);
  const body = grid.slice(headerRow + 1);

  const dateValues = body.map((r) => r[columns.date] ?? '').filter(Boolean);
  const detected = detectDateOrder(dateValues);
  const order = forcedOrder ?? detected.order;

  const rows: ParsedRow[] = [];
  const skipped: ParseResult['skipped'] = [];
  let lastYear: number | undefined;

  body.forEach((cells, i) => {
    const line = headerRow + 2 + i;
    const rawDate = (cells[columns.date] ?? '').trim();
    const rawHours = (cells[columns.hours] ?? '').trim();
    const notes = columns.notes >= 0 ? (cells[columns.notes] ?? '').trim() : '';

    // Filler rows: the ActiveFence sheet ends with ~30 of these, month label
    // only, no date and no hours. Not worth reporting as failures.
    if (!rawDate && !rawHours) return;

    const parsed = parseLegacyDate(rawDate, order, lastYear);
    if (!parsed) {
      skipped.push({ line, raw: cells.join(' | ').slice(0, 120), reason: 'Unreadable date' });
      return;
    }

    const hours = Number(rawHours.replace(',', '.'));
    if (!Number.isFinite(hours) || hours <= 0) {
      skipped.push({ line, raw: cells.join(' | ').slice(0, 120), reason: 'Missing or zero hours' });
      return;
    }

    lastYear = Number(parsed.date.slice(0, 4));
    rows.push({ date: parsed.date, hours, notes, warning: parsed.warning });
  });

  return { rows, skipped, dateOrder: order, ambiguous: !forcedOrder && detected.ambiguous, columns };
}

export function toEntryInputs(rows: ParsedRow[], clientId: string): EntryInput[] {
  return rows.map((r) => ({ clientId, date: r.date, hours: r.hours, notes: r.notes }));
}
